// Router factory + type-registry augmentation.
//
// TanStack Router's `Register` interface is the canonical hook to feed the
// concrete router type into `useNavigate`, `Link`, etc. We compute a stub
// router at module-load time purely for the type — `App.tsx` mints the real
// router with the correct `apiClient` context per render. The stub never
// renders, so its `apiClient` value is intentionally a no-op shim.

import { createRouter } from '@tanstack/react-router';

import { getClientBaseUrl, getClientFetch, type ApiClient } from '@barel/api-client';

import { routeTree } from '@/app/routeTree';
import type { RouterContext } from '@/app/__root';

/**
 * Build a real router instance. We extract the wrapped fetch from the typed
 * client *here* (where the api-client module identity is guaranteed) and
 * thread it through the router context so `beforeLoad` can't drift onto
 * `globalThis.fetch` if the consumer module instance loses the WeakMap.
 */
export function createAppRouter(apiClient: ApiClient) {
  const baseUrl = getClientBaseUrl(apiClient);
  const wrappedFetch = getClientFetch(apiClient);
  const authFetch: RouterContext['authFetch'] = (path, init) =>
    wrappedFetch(`${baseUrl}${path}`, init);
  return createRouter({
    routeTree,
    context: { apiClient, authFetch } satisfies RouterContext,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
  });
}

// Type-only stub used solely to feed the concrete router type into the
// `Register` interface. The context values are coerced — the stub is never
// rendered. The leading underscore silences the unused-var lint rule; the
// type alias is the actual hand-off into the `Register` augmentation below.
const _typeOnlyRouter = createRouter({
  routeTree,
  context: {
    apiClient: undefined as unknown as ApiClient,
    authFetch: (() => Promise.resolve(new Response())) as RouterContext['authFetch'],
  },
});

export type AppRouter = typeof _typeOnlyRouter;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
